import { ArrowLeftIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { ButtonWizard, LinkWizard, LinkWizardSo, TextInput } from '../../components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import globeImage from '../../assets/images/globe1.png';
import { KeySquare } from 'lucide-react';
import { useResetPasswordMutation } from '../../store';
import { useNotification } from '../../contexts';
import { extractErrorMessageFromAPI } from '../../utils';
import PublicAuthLayout from './PublicAuthLayout';
import softgelLogoImg from '../../assets/images/softgel_logo.png';
import softgelBgImg from '../../assets/images/softgel_bg.png';
import denLogo from '../../assets/images/den_logo_image.svg';
import hathLogo from '../../assets/images/hathway_logo.png';
import { encryptPassword } from '../../utils/encryption';
import PasswordStrengthMeter from '../../components/PasswordStrengthMeter';
import { validatePasswordPolicy } from '../../utils/passwordPolicy';

export interface ISigninForm {
  password: string;
  confirmPassword: string;
  otp: number;
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { control, formState, handleSubmit, getValues, watch } =
    useForm<ISigninForm>({
      defaultValues: {
        password: '',
        confirmPassword: '',
        otp: null,
      },
    });

  async function handleSubmitForm(data: ISigninForm) {
    // Validate token exists
    if (!token) {
      showNotification(
        'error',
        'Error',
        'Invalid or missing reset token. Please request a new password reset link.',
      );
      return;
    }

    try {
      data.password = encryptPassword(data.password),
      data.confirmPassword = encryptPassword(data.confirmPassword),
      await resetPassword({ token, ...data }).unwrap();
      showNotification('success', 'Success', 'Password reset successfully');
      navigate('/signin');
    } catch (error) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(error) ||
          'Failed to reset password. Please try again.',
      );
      console.error('Reset password error:', error);
    }
  }

  const clientCode = import.meta.env.VITE_ENV_CLIENT_CODE;
  const isSoftgel = clientCode === 'SOFTGEL';
  const logoSrc = isSoftgel
    ? softgelLogoImg
    : clientCode === 'HATHWAY'
      ? hathLogo
      : denLogo;
  const primaryBtnClass = isSoftgel
    ? 'bg-[#9d0b2d] hover:bg-[#7f0823]'
    : 'bg-blue-600 hover:bg-blue-700';

  const rightInner = (
    <div className="w-full max-w-md space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-1">
          Reset your password
        </h2>
        <p className="text-xs text-gray-600">
          Enter the code from your email and new password
        </p>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <form className="space-y-3">
          <TextInput
            name={'otp'}
            control={control}
            rules={{
              required: 'Verification code is required',
              minLength: {
                value: 6,
                message: 'Code must be exactly 6 digits',
              },
              maxLength: {
                value: 6,
                message: 'Code must be exactly 6 digits',
              },
              pattern: {
                value: /^\d{6}$/,
                message: 'Code must contain only numbers',
              },
            }}
            label={'Verification Code'}
            variant="text"
            labelClassName="text-xs font-medium text-gray-700"
            formState={formState}
            config={{
              prefix: <KeySquare className="w-4 h-4 text-gray-400" />,
              placeholder: '000000',
              className: 'h-10 text-sm',
            }}
          />

          <TextInput
            name={'password'}
            control={control}
            rules={{
              required: 'Password is required',
              validate: (value: string | number) => {
                return validatePasswordPolicy(String(value));
              },
            }}
            label={'New Password'}
            variant="password"
            labelClassName="text-xs font-medium text-gray-700"
            formState={formState}
            config={{
              prefix: <LockClosedIcon className="w-4 h-4 text-gray-400" />,
              placeholder: 'Enter new password',
              className: 'h-10 text-sm',
            }}
          />

          <PasswordStrengthMeter password={watch('password')} />

          <TextInput
            name={'confirmPassword'}
            control={control}
            rules={{
              required: 'Please confirm your password',
              validate: value => {
                if (value !== getValues('password')) {
                  return 'Passwords do not match';
                }
                return true;
              },
            }}
            label={'Confirm Password'}
            variant="password"
            labelClassName="text-xs font-medium text-gray-700"
            formState={formState}
            config={{
              prefix: <LockClosedIcon className="w-4 h-4 text-gray-400" />,
              placeholder: 'Confirm new password',
              className: 'h-10 text-sm',
            }}
          />

          <ButtonWizard
            onBtnClick={handleSubmit(handleSubmitForm)}
            addOnClass={`w-full h-10 ${primaryBtnClass} text-white rounded-lg font-medium text-sm transition-all duration-200 shadow-sm hover:shadow-md`}
            loading={isLoading}
          >
            Reset Password
          </ButtonWizard>

          <div className="pt-3 border-t border-gray-200">
            {isSoftgel ? (
              <LinkWizardSo
                href="/signin"
                addOnClass="flex items-center justify-center gap-2 text-xs font-medium"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to login
              </LinkWizardSo>
            ) : (
              <LinkWizard
                href="/signin"
                addOnClass="flex items-center justify-center gap-2 text-xs font-medium text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to login
              </LinkWizard>
            )}
          </div>
        </form>
      </div>

      {/* Footer */}
      <p className="text-center text-xs text-gray-500">
        &copy; {new Date().getFullYear()}{' '}
        <Link
          target="_blank"
          to="https://sapphiretech.co.in"
          className="text-gray-600 hover:text-blue-600 transition-colors font-medium"
          rel="noopener noreferrer"
        >
          Sapphire Technocrats Pvt Ltd.
        </Link>{' '}
        &mdash; All rights reserved
      </p>
    </div>
  );

  if (isSoftgel) {
    return (
      <div className="h-screen overflow-hidden">
        <PublicAuthLayout
          logoSrc={logoSrc}
          leftBgImageSrc={softgelBgImg}
          accentTextClass="text-teal-500"
        >
          {rightInner}
        </PublicAuthLayout>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex font-sans bg-gray-50">
      {/* Left Side - Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-2/5 relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
        </div>

        {/* Globe Image */}
        <img
          src={globeImage}
          alt="Globe"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-auto opacity-20 pointer-events-none"
          loading="eager"
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-16 py-20 text-white">
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="items-center justify-cente relative -top-10">
                <img
                  src={logoSrc}
                  alt="Client logo"
                  className="w-24 h-18"
                  loading="eager"
                />
              </div>

              <div className="space-y-4 max-w-lg">
                <h1 className="text-6xl font-bold leading-[1.1] tracking-tight">
                  Enterprise Portal
                </h1>
                <div className="h-1 w-20 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></div>
                <p className="text-xl text-blue-100 leading-relaxed">
                  Create new password
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Reset Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        {rightInner}
      </div>
    </div>
  );
}

// import { ArrowLeftIcon, LockClosedIcon } from '@heroicons/react/24/outline';
// import { useForm } from 'react-hook-form';
// import { ButtonWizard, HeadingWizard, LinkWizard, TextInput } from '../../components';
// import { useNavigate, useSearchParams } from 'react-router-dom';
// import signInImage from '../../assets/images/signIn.svg';
// import denLogo from '../../assets/images/den_logo.png';
// import { KeySquare } from 'lucide-react';
// import { useResetPasswordMutation } from '../../store';
// import { useNotification } from '../../contexts';
// import { extractErrorMessageFromAPI } from '../../utils';

// export interface ISigninForm {
//   password: string;
//   confirmPassword: string;
//   otp: number;
// }
// export default function ResetPassword() {
//   const [searchParams] = useSearchParams();
//   const token = searchParams.get('token');
//   const [resetPassword, { isLoading} ] = useResetPasswordMutation();
//   const { showNotification } = useNotification();
//   const navigate = useNavigate();
//   const { control, formState, handleSubmit, getValues } = useForm<ISigninForm>({
//     defaultValues: {
//       password: '',
//       confirmPassword: '',
//       otp: null,
//     },
//   });

//   async function handleSubmitForm(data: ISigninForm) {
//     try {
//       await resetPassword({token, ...data}).unwrap();
//       showNotification(
//         'success',
//         'Success',
//         'Password reset successfully',
//       );
//       navigate('/signin');
//     } catch (error) {
//       showNotification('error', 'Error', extractErrorMessageFromAPI(error));
//       console.error(error);
//     }
//   }

//   return (
//     <div className="h-screen grid sm:grid-cols-2 grid-cols-1">
//       <div className="overflow-hidden flex flex-col items-center justify-center bg-slate-200">
//         <div
//           className="w-96 rounded-lg py-12 px-12 flex flex-col gap-8 bg-white"
//           style={{
//             boxShadow: 'rgba(0, 0, 0, 0.15) 0px 2px 8px',
//           }}
//         >
//           <div className="flex items-center gap-2 justify-center">
//             <img src={denLogo} alt="logo" className="w-18" />
//           </div>
//           <div className="">
//             <HeadingWizard addOnClass="text-slate-800 mb-1">
//               Enter password reset details
//             </HeadingWizard>
//             <form className="flex flex-col gap-2 mt-4">
//               <TextInput
//                 name={'otp'}
//                 control={control}
//                 rules={{
//                   required: '* This field is required',
//                   minLength: {
//                     value: 6,
//                     message: 'OTP must be exactly 6 digits',
//                   },
//                   maxLength: {
//                     value: 6,
//                     message: 'OTP must be exactly 6 digits',
//                   },
//                   pattern: {
//                     value: /^\d{6}$/,
//                     message: 'OTP must contain only numbers',
//                   },
//                 }}
//                 label={'Verification Code'}
//                 variant="text"
//                 formState={formState}
//                 config={{
//                   prefix: <KeySquare className="size-4 text-text-600" />,
//                   placeholder: 'Enter 6 digit code',
//                 }}
//               />

//               <TextInput
//                 name={'password'}
//                 control={control}
//                 rules={{
//                   required: '* This field is required',
//                   minLength: {
//                     value: 8,
//                     message: 'Password must be at least 8 characters long',
//                   },
//                 }}
//                 label={'Password'}
//                 variant="password"
//                 formState={formState}
//                 config={{
//                   prefix: <LockClosedIcon className="size-4 text-text-600" />,
//                   placeholder: '**************',
//                 }}
//               />

//               <TextInput
//                 name={'confirmPassword'}
//                 control={control}
//                 rules={{
//                   required: '* This field is required',
//                   minLength: {
//                     value: 8,
//                     message: 'Password must be at least 8 characters long',
//                   },
//                   validate: value =>
//                     value === getValues('password') || 'Passwords do not match',
//                 }}
//                 label={'Confirm password'}
//                 variant="password"
//                 formState={formState}
//                 config={{
//                   prefix: <LockClosedIcon className="size-4 text-text-600" />,
//                   placeholder: '**************',
//                 }}
//               />

//               <ButtonWizard
//                 onBtnClick={handleSubmit(handleSubmitForm)}
//                 addOnClass={
//                   'bg-primary-600 text-white py-2 text-xs rounded mb-8'
//                 }
//                 loading={isLoading}
//                 >
//                               Reset Password
//                             </ButtonWizard>
//               {/* <button
//                 onClick={handleSubmit(handleSubmitForm)}
//                 className="border border-primary-600 text-primary-600 py-2 mt-2 text-xs rounded"
//               >
//                 Resend Verification Code
//               </button> */}
//               <LinkWizard href={`/signin`} addOnClass="font-bold">
//                 <>
//                   <ArrowLeftIcon className="size-3" />
//                   Back to log in
//                 </>
//               </LinkWizard>
//             </form>
//           </div>
//         </div>
//       </div>
//       <div className="flex items-center justify-center border-l bg-slate-700">
//         <div className="h-96 w-96">
//           <img src={signInImage} alt="Sign In Image" loading="eager" />
//         </div>
//       </div>
//     </div>
//   );
// }
